// API Response Caching Service
// Netflix-style API response caching for peak performance

import { advancedCache } from './advancedCache';
import { logger } from '../utils/logger';

export interface CachedAPIResponse<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

const DEFAULT_TTL = {
  userProgress: 5 * 60 * 1000,
  analytics: 1 * 60 * 1000,
  leaderboard: 10 * 60 * 1000,
  recommendations: 30 * 60 * 1000,
  default: 5 * 60 * 1000
} as const;

type CacheType = keyof typeof DEFAULT_TTL;

class APICacheService {
  async getCachedResponse<T>(
    endpoint: string,
    params: Record<string, any> = {},
    _cacheType: CacheType = 'default'
  ): Promise<T | null> {
    const cacheKey = this.generateCacheKey(endpoint, params);
    
    try {
      // Check advanced cache first
      const cached = await advancedCache.get<CachedAPIResponse<T>>(cacheKey, 'default');
      
      if (cached) {
        // Check if cache is still valid
        const age = Date.now() - cached.timestamp;
        if (age < cached.ttl) {
          logger.debug(`API cache HIT: ${endpoint} (age: ${Math.round(age / 1000)}s)`);
          return cached.data;
        } else {
          // Cache expired, remove it
          await this.invalidateCache(endpoint, params);
          logger.debug(`API cache EXPIRED: ${endpoint}`);
        }
      }
      
      logger.debug(`API cache MISS: ${endpoint}`);
      return null;
    } catch (error) {
      logger.error('Error getting cached API response:', error);
      return null;
    }
  }

  /**
   * Set cached API response
   */
  async setCachedResponse<T>(
    endpoint: string,
    params: Record<string, any> = {},
    data: T,
    cacheType: CacheType = 'default',
    customTTL?: number
  ): Promise<void> {
    const cacheKey = this.generateCacheKey(endpoint, params);
    const ttl = customTTL || DEFAULT_TTL[cacheType] || DEFAULT_TTL.default;
    
    try {
      const cachedResponse: CachedAPIResponse<T> = {
        data,
        timestamp: Date.now(),
        ttl
      };
      
      await advancedCache.set(cacheKey, cachedResponse, 'default');
      logger.debug(`API response cached: ${endpoint} (TTL: ${Math.round(ttl / 1000)}s)`);
    } catch (error) {
      logger.error('Error caching API response:', error);
    }
  }

  /**
   * Invalidate cached API response
   */
  async invalidateCache(
    endpoint: string,
    params: Record<string, any> = {}
  ): Promise<void> {
    const cacheKey = this.generateCacheKey(endpoint, params);
    
    try {
      await advancedCache.invalidate(cacheKey);
      logger.debug(`API cache invalidated: ${endpoint}`);
    } catch (error) {
      logger.error('Error invalidating API cache:', error);
    }
  }

  /**
   * Invalidate all cached responses for an endpoint pattern
   */
  async invalidatePattern(pattern: string): Promise<void> {
    try {
      await advancedCache.invalidatePattern(`api_${pattern}`);
      logger.debug(`API cache pattern invalidated: ${pattern}`);
    } catch (error) {
      logger.error('Error invalidating API cache pattern:', error);
    }
  }

  /**
   * Generate cache key from endpoint and params
   */
  private generateCacheKey(endpoint: string, params: Record<string, any>): string {
    // Sort params for consistent keys
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}:${JSON.stringify(params[key])}`)
      .join('|');
    
    return `api_${endpoint}_${sortedParams}`;
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<{
    hitRate: number;
    missRate: number;
    totalRequests: number;
  }> {
    // This would track hit/miss rates
    // For now, return placeholder
    return {
      hitRate: 0,
      missRate: 0,
      totalRequests: 0
    };
  }
}

export const apiCache = new APICacheService();

